// (c) chaehongee. Confidential and proprietary.

import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../../store/hooks/useAuth'
import { updateUserProfile } from '../../../services/user'
import type { UserResponse } from 'models-ts/user'
import { LanguageSwitcher } from '../../LanguageSwitcher'
import { ThemeSwitcher } from '../../ThemeSwitcher'
import styles from './PageLayout.module.css'
import { TriangleAlert } from 'lucide-react'

interface AccountSettingsProps {
    userProfile: UserResponse | null
    isLoadingProfile: boolean
    onProfileUpdate: (profile: UserResponse) => void
}

export function AccountSettings({
    userProfile,
    isLoadingProfile,
    onProfileUpdate,
}: AccountSettingsProps) {
    const { user } = useAuth()
    const { t } = useTranslation('dashboard')
    const [isEditing, setIsEditing] = useState(false)
    const [displayName, setDisplayName] = useState('')
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        if (userProfile) {
            setDisplayName(userProfile.display_name || '')
        }
    }, [userProfile])

    const handleSave = async () => {
        if (!displayName.trim()) {
            setError(t('displayNameEmpty'))
            return
        }

        setLoading(true)
        setError(null)

        try {
            const updated = await updateUserProfile({
                display_name: displayName.trim(),
            })
            onProfileUpdate(updated)
            setIsEditing(false)
        } catch (err) {
            console.error('Failed to update profile:', err)
            setError(t('failedToUpdateProfile'))
        } finally {
            setLoading(false)
        }
    }

    const handleCancel = () => {
        setDisplayName(userProfile?.display_name || '')
        setIsEditing(false)
        setError(null)
    }

    if (isLoadingProfile || !userProfile) {
        return (
            <div className={styles.page}>
                <div className={styles.section}>
                    <p className={styles.placeholder}>{t('common:loading')}</p>
                </div>
            </div>
        )
    }

    return (
        <div className={styles.page}>
            <div className={styles.section}>
                <h3 className={styles.title}>{t('profile')}</h3>

                {/* Display Name */}
                <div className={styles.row}>
                    <span className={styles.label}>{t('displayName')}</span>
                    {isEditing ? (
                        <div className={styles.editContainer}>
                            <input
                                type="text"
                                value={displayName}
                                onChange={(e) => setDisplayName(e.target.value)}
                                className={styles.input}
                                placeholder={t('displayName')}
                                maxLength={100}
                            />
                            <div className={styles.buttonGroup}>
                                <button
                                    onClick={handleSave}
                                    disabled={loading}
                                    className={styles.saveButton}
                                >
                                    {loading ? t('saving') : t('common:save')}
                                </button>
                                <button
                                    onClick={handleCancel}
                                    disabled={loading}
                                    className={styles.cancelButton}
                                >
                                    {t('common:cancel')}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className={styles.valueWithAction}>
                            <span className={styles.value}>
                                {userProfile.display_name || t('notSet')}
                            </span>
                            <button
                                onClick={() => setIsEditing(true)}
                                className={styles.editButton}
                            >
                                {t('edit')}
                            </button>
                        </div>
                    )}
                </div>

                {error && <div className={styles.error}>{error}</div>}

                {/* Email (read-only) */}
                <div className={styles.row}>
                    <span className={styles.label}>{t('auth:email')}</span>
                    <span className={styles.value}>{user?.email || t('notAvailable')}</span>
                </div>

                {/* Theme */}
                <div className={styles.row}>
                    <span className={styles.label}>{t('theme')}</span>
                    <ThemeSwitcher />
                </div>

                {/* Language */}
                <div className={styles.row}>
                    <span className={styles.label}>{t('language')}</span>
                    <LanguageSwitcher />
                </div>
            </div>

            {/* Danger Zone */}
            <div className={styles.dangerZone}>
                <h3 className={styles.dangerZoneTitle}>{t('dangerZone')}</h3>
                <div className={styles.dangerZoneContent}>
                    <div className={styles.dangerZoneHeader}>
                        <TriangleAlert className={styles.dangerIcon} />
                        <span className={styles.dangerZoneHeading}>
                            {t('deleteAccountHeading')}
                        </span>
                    </div>
                    <p className={styles.dangerZoneDescription}>
                        {t('deleteAccountDescription')}
                    </p>
                    <button className={styles.deleteButton}>
                        {t('deleteAccountButton')}
                    </button>
                </div>
            </div>
        </div>
    )
}
